import React from 'react';
import { useIntl } from 'react-intl';
import { getDealRoomBrandedName, getDealRoomSingleWord } from '../../utils/locale';
import { gql, useQuery } from '@apollo/client';
import { Target as TargetIcon, ArrowRight as ArrowRightIcon } from 'lucide-react';

const GET_BUYER_DEAL_INTERESTS = gql`
  query GetBuyerDealInterests {
    getCurrentUser {
      id
      company { id deal_room_branded_name deal_room_branded_name_fr deal_room_single_word deal_room_single_word_fr }
      inventory_user {
        id
        filtered_deal_interests {
          id
          workflow_state
          invitation_date
          deal {
            id
            deal_name
            headline
            list_price
            deal_state
            closed
            inventory_user {
              user { allowed_name city province }
            }
          }
        }
      }
    }
  }
`;

// Workflow stage keys for the progress stepper (5 dots) — labels resolved via intl at render time
const WORKFLOW_STAGE_KEYS = [
  { key: 'invited',            intlId: 'succession.buyer_room.progress_tracker.stage_invited' },
  { key: 'reviewing_proposal', intlId: 'succession.buyer_room.progress_tracker.stage_proposal' },
  { key: 'shortlisted',        intlId: 'succession.buyer_room.progress_tracker.stage_shortlisted' },
  { key: 'nda',                intlId: 'succession.buyer_room.progress_tracker.stage_nda' },
  { key: 'negotiating',        intlId: 'succession.buyer_room.progress_tracker.stage_negotiating' },
];

// Map actual workflow_state values to a stage index (0-4)
const getStageIndex = (state) => {
  const stageMap = {
    invited: 0,
    reviewing_proposal: 1,
    shortlisted: 2,
    out_for_receiving_signature: 3,
    awaiting_disclosing_signature: 3,
    negotiating: 4,
  };
  return stageMap[state] ?? -1;
};

// Status display config for each workflow state — label resolved via intl at render time
// Uses inline styles because Tailwind CDN v2 doesn't include amber/orange and
// dynamic class concatenation may not be picked up by JIT purging.
const STATUS_STYLE_CONFIG = {
  invited:                      { intlId: 'succession.buyer_room.workflow_controls.state_invited',                      badgeStyle: { backgroundColor: '#DBEAFE', color: '#1D4ED8' }, dotStyle: { backgroundColor: '#3B82F6' } },
  reviewing_proposal:           { intlId: 'succession.buyer_room.workflow_controls.state_reviewing_proposal',           badgeStyle: { backgroundColor: '#FEF3C7', color: '#B45309' }, dotStyle: { backgroundColor: '#F59E0B' } },
  shortlisted:                  { intlId: 'succession.buyer_room.workflow_controls.state_shortlisted',                  badgeStyle: { backgroundColor: '#EDE9FE', color: '#6D28D9' }, dotStyle: { backgroundColor: '#8B5CF6' } },
  out_for_receiving_signature:  { intlId: 'succession.buyer_room.workflow_controls.state_out_for_receiving_signature',  badgeStyle: { backgroundColor: '#FFEDD5', color: '#C2410C' }, dotStyle: { backgroundColor: '#F97316' } },
  awaiting_disclosing_signature:{ intlId: 'succession.buyer_room.workflow_controls.state_awaiting_disclosing_signature',badgeStyle: { backgroundColor: '#FFEDD5', color: '#C2410C' }, dotStyle: { backgroundColor: '#F97316' } },
  negotiating:                  { intlId: 'succession.buyer_room.workflow_controls.state_negotiating',                  badgeStyle: { backgroundColor: '#D1FAE5', color: '#047857' }, dotStyle: { backgroundColor: '#10B981' } },
};

// States that count as "active" for buyer deal interests
const EXCLUDED_STATES = ['matching', 'declined', 'declined_by_buyer', 'archived'];

const pluralizeLabel = (label) => {
  const trimmed = String(label || '').trim();
  if (!trimmed) return 'Deal Rooms';
  return trimmed.endsWith('s') ? trimmed : `${trimmed}s`;
};

// Mini progress stepper component
const ProgressStepper = ({ furthestStageIndex, stages }) => {
  return (
    <div className="flex items-center justify-between">
      {stages.map((stage, index) => (
        <div key={stage.key} className="flex items-center flex-1">
          <div className="flex flex-col items-center flex-1" style={{ gap: '6px' }}>
            <div
              className={`w-2.5 h-2.5 rounded-full transition-colors ${
                index <= furthestStageIndex
                  ? 'bg-succession-primary'
                  : 'bg-succession-bg-tertiary'
              }`}
            />
            <span
              className={`text-center font-medium ${
                index <= furthestStageIndex
                  ? 'text-succession-text-primary'
                  : 'text-succession-text-muted'
              }`}
              style={{ fontSize: '10px' }}
            >
              {stage.label}
            </span>
          </div>
          {index < stages.length - 1 && (
            <div
              className={`h-0.5 flex-1 ${
                index < furthestStageIndex
                  ? 'bg-succession-primary'
                  : 'bg-succession-bg-tertiary'
              }`}
              style={{ margin: '0 -4px' }}
            />
          )}
        </div>
      ))}
    </div>
  );
};

const BuyerDealInterestsCard = ({ className = '', showHeaderIcon = true }) => {
  const intl = useIntl();
  const { data, loading, error } = useQuery(GET_BUYER_DEAL_INTERESTS, { fetchPolicy: 'cache-first' });

  if (loading) {
    return (
      <div className={`bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border p-6 h-full ${className}`}>
        <div className="flex items-start gap-4">
          <div className="flex-1 space-y-2">
            <div className="h-5 bg-succession-bg-secondary rounded w-24 animate-pulse" />
            <div className="h-4 bg-succession-bg-secondary rounded w-40 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border p-6 h-full ${className}`}>
        <div className="text-sm text-succession-error">{intl.formatMessage({ id: 'succession.launchpad.unable_to_load_deal_interests' })}</div>
      </div>
    );
  }

  const cu = data?.getCurrentUser || {};
  const allInterests = cu?.inventory_user?.filtered_deal_interests || [];

  // Filter to only active states (>= invited, excluding matching/declined/archived)
  const activeDealInterests = allInterests.filter(
    (di) => !EXCLUDED_STATES.includes(di.workflow_state)
  );

  // Return null when there are no active interests (used inside LaunchPad grid)
  if (activeDealInterests.length === 0) {
    return null;
  }

  const dealRoomName = getDealRoomBrandedName(cu?.company) || intl.formatMessage({ id: 'succession.messaging.chat_panel.deal_room_label' });
  const word = getDealRoomSingleWord(cu?.company) || dealRoomName;
  const dealRoomPlural = pluralizeLabel(dealRoomName);
  const furthestStageIndex = Math.max(
    ...activeDealInterests.map((di) => getStageIndex(di.workflow_state))
  );

  // Build translated stages and status configs using intl
  const workflowStages = WORKFLOW_STAGE_KEYS.map(s => ({ key: s.key, label: intl.formatMessage({ id: s.intlId }) }));
  const getStatusConfig = (state) => {
    const cfg = STATUS_STYLE_CONFIG[state] || STATUS_STYLE_CONFIG.invited;
    return { ...cfg, label: intl.formatMessage({ id: cfg.intlId }) };
  };
  const furthestStageLabel = workflowStages[furthestStageIndex]?.label || workflowStages[0].label;

  return (
    <div className={`bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg border border-succession-border p-6 hover:shadow-succession-lg transition-shadow h-full ${className}`}>
      {/* Header */}
      <div className="mb-6">
        {showHeaderIcon ? (
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-succession-primary/10">
              <TargetIcon className="w-6 h-6 text-succession-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>{intl.formatMessage({ id: 'succession.launchpad.my_opportunities_title' })}</h3>
              <p className="text-sm text-succession-text-secondary leading-tight" style={{ margin: 0, padding: 0 }}>
                {intl.formatMessage({ id: 'succession.launchpad.my_opportunities_subtitle' }, { word: word.toLowerCase() })}
              </p>
            </div>
          </div>
        ) : (
          <>
            <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>{intl.formatMessage({ id: 'succession.launchpad.my_opportunities_title' })}</h3>
            <p className="text-sm text-succession-text-secondary leading-tight" style={{ margin: 0, padding: 0 }}>
              {intl.formatMessage({ id: 'succession.launchpad.my_opportunities_subtitle' }, { word: word.toLowerCase() })}
            </p>
          </>
        )}
      </div>

      {/* Deal List */}
      <div className="mb-5 -mx-2 px-2 overflow-y-auto" style={{ maxHeight: '280px' }}>
        <div className="space-y-3">
          {activeDealInterests.map((interest) => {
            const statusConfig = getStatusConfig(interest.workflow_state);
            const seller = interest.deal?.inventory_user?.user;
            const sellerName = seller?.allowed_name || 'Seller';
            const city = seller?.city || '';
            const province = seller?.province || '';
            const location = [city, province].filter(Boolean).join(', ');

            return (
              <a
                key={interest.id}
                href={`/succession/deals/${interest.deal?.deal_name}/buyer_room`}
                className="block rounded-lg border border-succession-border hover:bg-succession-bg-secondary transition-colors"
                style={{ padding: '14px 16px' }}
              >
                <div className="flex items-center gap-3">
                  {/* Status Dot */}
                  <div className="flex-shrink-0">
                    <div
                      className="rounded-full"
                      style={{ width: '10px', height: '10px', ...statusConfig.dotStyle }}
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-succession-text-primary" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '2px' }}>
                      {interest.deal?.headline || intl.formatMessage({ id: 'succession.launchpad.untitled_deal' })}
                    </h4>
                    <p className="text-xs text-succession-text-secondary" style={{ margin: 0 }}>
                      {sellerName}{location ? ` \u00B7 ${location}` : ''}
                    </p>
                  </div>

                  {/* Status Badge */}
                  <span
                    className="text-xs font-medium flex-shrink-0 rounded-full"
                    style={{ padding: '3px 10px', ...statusConfig.badgeStyle }}
                  >
                    {statusConfig.label}
                  </span>
                </div>
              </a>
            );
          })}
        </div>
      </div>

      {/* Mini Progress Stepper */}
      <div className="mb-5 py-4 border-t border-succession-border">
        <ProgressStepper furthestStageIndex={furthestStageIndex} stages={workflowStages} />
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-6 mb-6 pb-6 border-b border-succession-border">
        <div>
          <div className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.launchpad.active_opportunities' })}</div>
          <div className="text-2xl font-bold text-succession-text-primary">{activeDealInterests.length}</div>
        </div>
        <div>
          <div className="text-sm text-succession-text-secondary mb-1">{intl.formatMessage({ id: 'succession.launchpad.furthest_stage' })}</div>
          <div className="text-2xl font-bold text-succession-text-primary">{furthestStageLabel}</div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end">
        <a
          href="/succession/deals"
          className="inline-flex items-center text-sm font-medium text-succession-primary hover:text-succession-primary-hover transition-colors group p-0"
        >
          {intl.formatMessage({ id: 'succession.launchpad.view_all_deals' })}
          <ArrowRightIcon className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
        </a>
      </div>
    </div>
  );
};

export default BuyerDealInterestsCard;
